import { z } from "zod";

export const ProviderPathSchema = z.enum([
  "liveavatar-direct",
  "liveavatar-livekit",
  "simli-p2p",
  "simli-livekit",
  "bithuman-cloud",
  "bithuman-self-hosted",
  "beyond-livekit",
  "did-echo",
  "runway-livekit",
  "tavus-livekit",
]);
export const ProfileSchema = z
  .object({
    id: z.string(),
    version: z.string(),
    name: z.string(),
    voiceId: z.string(),
    voiceModel: z.string().min(1).max(80).default("s2.1-pro"),
    speechStyle: z.string().max(300).nullable().default(null),
    portraitUrl: z.string().nullable(),
    portraitStatus: z.enum(["missing", "uploaded", "authorized", "revoked"]),
    synthetic: z.literal(true),
    portraitHash: z.string().nullable().default(null),
    primaryPath: ProviderPathSchema.default("liveavatar-direct"),
    fallbackPaths: z.array(ProviderPathSchema).default([]),
    providerAssets: z.partialRecord(ProviderPathSchema, z.string().max(500)).default({}),
    /** Operator-authored persona for live answers; the grounding policy always precedes it. */
    answerPrompt: z.string().max(4000).nullable().default(null),
    locale: z.literal("ko-KR").default("ko-KR"),
    status: z.enum(["draft", "active", "revoked"]).default("draft"),
  })
  .readonly();
export const LearnerProfileSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    voiceId: z.string(),
    portraitUrl: z.string().nullable(),
  })
  .readonly();
export type LearnerProfile = z.infer<typeof LearnerProfileSchema>;
export const ProfileInputSchema = z
  .strictObject({
    name: z.string().trim().min(1).max(120),
    // Fish cloned-voice reference ids are 32 hex characters; rejecting anything else here keeps a
    // typo from being saved and failing deep inside a build or a live start instead.
    voiceId: z
      .string()
      .trim()
      .toLowerCase()
      .regex(/^[a-f0-9]{32}$/, "Enter the 32-character Fish voice reference id."),
    voiceModel: z.string().trim().min(1).max(80).optional(),
    speechStyle: z.string().trim().max(300).nullable().optional(),
    answerPrompt: z.string().trim().max(4000).nullable().optional(),
    primaryPath: ProviderPathSchema,
    fallbackPaths: z.array(ProviderPathSchema).max(8),
    providerAssets: z.partialRecord(ProviderPathSchema, z.string().trim().min(1).max(500)),
    locale: z.literal("ko-KR"),
  })
  .refine(
    (value) =>
      !value.fallbackPaths.includes(value.primaryPath) &&
      new Set(value.fallbackPaths).size === value.fallbackPaths.length,
    { message: "Provider routes must be distinct." },
  )
  .readonly();
export const ProfileSettingsResponseSchema = z.object({
  profile: ProfileSchema,
  versions: z.array(z.object({ version: z.string(), createdAt: z.string() })),
  permalink: z.string(),
});
export type Profile = z.infer<typeof ProfileSchema>;
